<template>
  <div class="z-50 fixed inset-0 bg-gray-50 flex justify-center py-12 px-4">
    <div class="max-w-md w-full space-y-8">
      <div>
        <div
          class="
            cursor-default
            mx-auto
            w-14
            h-14
            bg-cyan-600
            rounded-xl
            text-white text-4xl
            font-semibold
            flex
            items-center
            justify-center
            focus:outline-none
          "
        >
          T
        </div>
        <h2 class="mt-6 text-center text-3xl font-bold text-gray-900">
          ลงชื่อเข้าใช้ระบบด้วยบัญชีของคุณ
        </h2>
      </div>

      <!-- Error Message -->
      <div
        v-if="errors.length > 0"
        class="px-4 py-3 rounded-md bg-red-600 bg-opacity-20 flex items-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 text-red-600"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
            clip-rule="evenodd"
          />
        </svg>
        <p class="text-base font-semibold text-red-800 flex-1 px-3">
          {{ errors[0] }}
        </p>
        <button @click="errors = []" type="button" class="focus:outline-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-red-600"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>
      <!-- End Error Message -->

      <div class="px-8 py-8 bg-white shadow rounded-md">
        <ValidationObserver v-slot="{ handleSubmit }">
          <form @submit.prevent="handleSubmit(onSubmit)" class="space-y-6">
            <div>
              <ValidationProvider
                name="ที่อยู่อีเมล"
                rules="required|email"
                v-slot="{ errors }"
              >
                <AppInput
                  v-model.trim="email"
                  label="ที่อยู่อีเมล"
                  type="email"
                  autofocus
                />
                <span class="text-base text-red-600">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <div class="relative">
              <ValidationProvider
                name="รหัสผ่าน"
                rules="required|max:128"
                v-slot="{ errors }"
              >
                <AppInput
                  v-model="password"
                  label="รหัสผ่าน"
                  :type="isShowPassword ? 'text' : 'password'"
                />
                <span class="text-base text-red-600">{{ errors[0] }}</span>
              </ValidationProvider>
              <button
                @click="isShowPassword = !isShowPassword"
                type="button"
                class="absolute top-0 right-0 text-cyan-600 hover:underline"
              >
                {{ isShowPassword ? 'ซ่อนรหัสผ่าน' : 'โชว์รหัสผ่าน' }}
              </button>
            </div>

            <div>
              <button
                :disabled="loading"
                type="submit"
                class="
                  group
                  relative
                  w-full
                  flex
                  justify-center
                  py-2
                  px-4
                  border border-transparent
                  text-base
                  font-medium
                  rounded-md
                  text-white
                  bg-cyan-600
                  hover:bg-cyan-700
                  focus:outline-none
                  focus:ring-2
                  focus:ring-offset-2
                  focus:ring-cyan-500
                "
                :class="{ 'cursor-not-allowed': loading }"
              >
                <span class="absolute left-0 inset-y-0 flex items-center pl-3">
                  <svg
                    v-if="loading"
                    class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>

                  <svg
                    v-else
                    class="h-5 w-5 text-cyan-500 group-hover:text-cyan-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </span>
                {{ loading ? 'กำลังประมวลผล..' : 'ล็อกอินเข้าระบบ' }}
              </button>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import AppInput from '@/components/AppInput.vue'
import { extend } from 'vee-validate'
import { required, max, email } from 'vee-validate/dist/rules'

// No message specified.
// extend('required', required)

// Override the default message.
extend('required', {
  ...required,
  message: 'จำเป็นต้องระบุ {_field_}',
})
extend('max', {
  ...max,
  params: ['length'],
  message: '{_field_} ต้องน้อยกว่า {length} ตัวอักษร',
})
extend('email', {
  ...email,
  params: ['length'],
  message: '{_field_} ไม่ถูกต้อง',
})

export default {
  name: 'Login',

  components: {
    AppInput,
  },

  data() {
    return {
      loading: false,
      email: '',
      password: '',
      errors: [],
      isShowPassword: false,
    }
  },

  methods: {
    async onSubmit() {
      try {
        this.loading = true

        const software3rdPartyCredential = {
          app_id: process.env.VUE_APP_APP_ID,
          secret: process.env.VUE_APP_SECRET,
          login_type_id: 1,
          timezone: moment().format('Z'),
        }

        await this.$store.dispatch(
          'auth/software3rdParty',
          software3rdPartyCredential
        )

        const loginCredential = {
          email: this.email,
          password: this.password,
          login_type_id: 1,
          timezone: moment().format('Z'),
        }

        await this.$store.dispatch('auth/login', loginCredential)

        this.$router.push({ name: 'Dashboard' })
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(
            error.response.data.message || 'Something went wrong!.'
          )
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
